import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from '@/api/index';
import { BASE_URL, BKC_URL, WOLT_BASE_URL } from '@/constants/index';
import { customFetchBaseQuery } from '@/api/numberSale';

export const numberSaleApi = createApi({
  reducerPath: 'numberSaleApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['NumberSale'],
  endpoints: (builder) => ({
    getNumbers: builder.query({
      query: ({ data }) => ({
        url: '/bff-sales/number-discovery/numbers',
        method: 'GET',
        headers: { 'Provider-Id': '2' },
        baseURL: BASE_URL,
        params: {
          ...data,
        },
      }),
    }),
    getTariffs: builder.query({
      query: ({ numberLevel, lang }) => ({
        url: '/prod/bakcell-tariff/tariffs',
        baseURL: BASE_URL,
        headers: { 'Accept-Language': lang },
        params: {
          numberSale: true,
          numberLevel,
        },
      }),
    }),
    getPackages: builder.query({
      query: ({ periodType, lang }) => ({
        url: '/prod/bakcell-packages/packages/number-sale',
        baseURL: BASE_URL,
        headers: { 'Accept-Language': lang },
        params: {
          periodType,
        },
      }),
    }),
    getBranches: builder.query({
      query: (lang) => ({
        baseURL: BASE_URL,
        headers: {
          'Content-Language': lang.toUpperCase(),
          'Provider-Id': 2,
        },
        url: 'v1/number-discovery-service/front-offices',
        method: 'GET',
      }),
      transformResponse: (response) => {
        return response?.map((item) => ({
          id: item?.salesOrganisationId,
          name: item?.officeName,
          address: item?.officeAddress,
          lat: item?.lat,
          lng: item?.lng,
        }));
      },
    }),
    checkIn: builder.mutation({
      async queryFn({ data, captcha, lang }) {
        return customFetchBaseQuery({
          url: 'v1/number-discovery-service/sales-number/check-in',
          method: 'POST',
          data,
          headers: {
            'Accept-Language': lang,
            'Provider-Id': 2,
            'g-recaptcha-response': captcha,
            'device-type': 'WEB',
          },
        });
      },
    }),
    simaCheck: builder.mutation({
      async queryFn({ formData, accessToken, verificationMethod, lang }) {
        return customFetchBaseQuery({
          url: 'v1/number-discovery-service/face-recognition/files/upload',
          method: 'POST',
          data: formData,
          headers: {
            'Verification-Method': verificationMethod,
            'Provider-Id': 2,
            Authorization: `Bearer ${accessToken}`,
            'Accept-Language': lang,
          },
        });
      },
    }),
    paymentRequest: builder.mutation({
      query: ({ lang, amount, redirectUrl, receiver = null }) => {
        return {
          baseURL: BASE_URL,
          url: '/prod/bakcell-payment/pay/without-card',
          method: 'POST',
          headers: { 'Accept-Language': lang },
          data: {
            amount,
            redirectUrl,
          },
          params: {
            receiver,
            web: true,
          },
        };
      },
    }),
    getVerificationResponse: builder.query({
      async queryFn(trackId) {
        return customFetchBaseQuery({
          url: `v1/number-discovery-service/sales-number/transactions/track-id/${trackId}/status`,
          baseURL: BKC_URL,
          headers: {
            'Provider-Id': 2,
          },
        });
      },
    }),
    bookNumber: builder.mutation({
      query: ({ data, accessToken }) => ({
        url: 'v1/number-discovery-service/sales-number/finalize',
        method: 'PUT',
        baseURL: BKC_URL,
        data,
        headers: {
          'Provider-Id': 2,
          Authorization: accessToken,
        },
      }),
    }),
    getNumberContract: builder.query({
      query: ({ trackId }) => ({
        url: `https://www.bakcell.com/api/v1/number-discovery-service/contracts/track-id/${trackId}/download`,
        method: 'GET',
        headers: {
          'Provider-Id': '2',
        },
        responseType: 'blob',
      }),
    }),
    getCustomerFullInfo: builder.mutation({
      async queryFn({ data, lang }) {
        return customFetchBaseQuery({
          url: 'v1/number-discovery-service/documents/full-name',
          method: 'POST',
          data,
          headers: {
            'Provider-Id': 2,
            'Accept-Language': lang,
          },
        });
      },
    }),
    addressDetail: builder.mutation({
      async queryFn({ data }) {
        return customFetchBaseQuery({
          url: 'v1/number-discovery-service/delivery/estimate',
          method: 'POST',
          data,
          headers: {
            'Content-Type': 'application/json',
            'Provider-Id': 2,
          },
        });
      },
    }),
    woltEstimateCheck: builder.mutation({
      query: ({ data, venueId, lang }) => ({
        url: `v1/delivery/estimate/${venueId}`,
        method: 'POST',
        baseURL: WOLT_BASE_URL,
        data,
        headers: {
          'Accept-Language': lang,
        },
      }),
    }),
  }),
});

export const hooks = {
  useGetNumbers: numberSaleApi.useGetNumbersQuery,
  useGetTariffs: numberSaleApi.useGetTariffsQuery,
  useGetPackages: numberSaleApi.useGetPackagesQuery,
  useGetBranches: numberSaleApi.useGetBranchesQuery,
  useCheckIn: numberSaleApi.useCheckInMutation,
  useSimaCheck: numberSaleApi.useSimaCheckMutation,
  usePaymentRequest: numberSaleApi.usePaymentRequestMutation,
  useGetVerificationResponse: numberSaleApi.useGetVerificationResponseQuery,
  useBookNumber: numberSaleApi.useBookNumberMutation,
  useGetLazyNumberContract: numberSaleApi.useLazyGetNumberContractQuery,
  useGetCustomerFullInfo: numberSaleApi.useGetCustomerFullInfoMutation,
  addressDetail: numberSaleApi.useAddressDetailMutation,
  woltEstimateCheck: numberSaleApi.useWoltEstimateCheckMutation,
};

export default numberSaleApi;
